import * as React from 'react';
import { CheckCircleFilled, EditOutlined, RocketOutlined } from '@ant-design/icons';
import Alert from 'antd/lib/alert';
import Button from 'antd/lib/button';
import Card from 'antd/lib/card';
import Descriptions from 'antd/lib/descriptions';
import Empty from 'antd/lib/empty';
import Layout, { Content, Footer } from 'antd/lib/layout/layout';
import Modal from 'antd/lib/modal';
import Select from 'antd/lib/select';
import Space from 'antd/lib/space';
import Spin from 'antd/lib/spin';
import Tag from 'antd/lib/tag';
import Text from 'antd/lib/typography/Text';
import { useSelector } from 'react-redux';
import { Link } from 'react-router-dom';
import * as API from '@/api';
import { ReleaseAppleVersionRequest, StepRunUpdatable } from '@/actions/stepRun';
import PermissionedActionButton from '@/components/HOCs/PermissionedActionButton';
import styles from '@/components/styles/stepStyles';
import { antGrey, releaseStepModuleTitleStyle, runwayColors } from '@/components/styles/globalStyles';
import { FetchableEntityTypeV2 } from '@/entityType';
import { dayjs } from '@/extendedDayjs';
import { RunwayUserAction } from '@/helpers/aclHelpers';
import { displayUserNameLong } from '@/helpers/displayHelpers';
import { createAppSettingsRouteWithSettingType } from '@/helpers/routeHelpers';
import { RootState } from '@/reducers/reducers';
import {
    selectAuthCredentials,
    selectIntegrationFromIntegrationType,
    selectIsAppsDemoModeEnabled,
    selectStepConfig,
    selectUserById,
} from '@/selectors/selectors';
import {
    ExpoEASCandidateUpdateGroup,
    ExpoEASReleaseStepData,
    ExpoEASSourceUpdateGroup,
    IntegrationExpoEAS,
    IntegrationType,
    ReleaseStepType,
    RunwayResponseType,
    SettingType,
} from '@/types';
import { StepRunComponentProps } from '@/typescriptHelpers';
import { globalMessage } from '@/utils/globalMessage';
import { useCallUpdatable, useGetEntityV2 } from '@/utils/hooks/helpers';

interface ExpoEASReleaseStepProps {
    orgId: string;
    appId: string;
    releaseId: string;
    displayableVersion: string;
    stepRunComponentProps: StepRunComponentProps;
    expoData?: ExpoEASReleaseStepData;
}

const expoReleaseCardStyle: React.CSSProperties = {
    backgroundColor: antGrey.grey2,
    border: `1px solid ${antGrey.grey5}`,
    borderRadius: 8,
    overflow: 'hidden',
};

const expoReleaseCardStyles = {
    header: {
        borderBottom: `1px solid ${antGrey.grey5}`,
    },
};

const hasProductionPublishData = (data?: ExpoEASReleaseStepData) =>
    !!data &&
    (!!data.productionGroupId ||
        Object.keys(data.productionUpdateIds ?? {}).length > 0 ||
        Object.keys(data.productionUpdatesByPlatform ?? {}).length > 0 ||
        data.initialRolloutPercentage != null ||
        data.currentRolloutPercentage != null ||
        !!data.publishedAt ||
        !!data.publishedBy);

const displayPlatform = (platform: string) => {
    switch (platform.toLowerCase()) {
        case 'ios':
            return 'iOS';
        case 'android':
            return 'Android';
        default:
            return platform;
    }
};

const candidatePlatforms = (candidate: ExpoEASCandidateUpdateGroup) =>
    Array.from(new Set(candidate.updates.map((update) => displayPlatform(update.platform))));

const sourcePlatforms = (source: ExpoEASSourceUpdateGroup) =>
    Array.from(
        new Set(Object.values(source.updatesByPlatform ?? {}).map((update) => displayPlatform(update.platform))),
    );

const UpdateGroupDetails = ({
    group,
    platforms,
    releasedByVersion,
}: {
    group: Pick<ExpoEASSourceUpdateGroup, 'branchName' | 'runtimeVersion' | 'message' | 'gitCommitHash' | 'createdAt'>;
    platforms: string[];
    releasedByVersion?: string;
}) => (
    <Space direction="vertical" size={4} style={{ width: '100%' }}>
        <Space wrap>
            <Text strong>{group.branchName}</Text>
            <Text type="secondary">Runtime {group.runtimeVersion}</Text>
            {platforms.map((platform) => (
                <Tag key={platform}>{platform}</Tag>
            ))}
        </Space>
        {group.message && <Text>{group.message}</Text>}
        <Space wrap size="middle">
            {group.gitCommitHash && (
                <Text type="secondary" code>
                    {group.gitCommitHash.slice(0, 12)}
                </Text>
            )}
            <Text type="secondary">Created {dayjs(group.createdAt).format('MMM D, YYYY [at] h:mm A')}</Text>
            {releasedByVersion && <Text type="secondary">Released by version {releasedByVersion}</Text>}
        </Space>
    </Space>
);

export const ExpoEASReleaseStep = ({
    orgId,
    appId,
    releaseId,
    displayableVersion,
    stepRunComponentProps,
    expoData,
}: ExpoEASReleaseStepProps) => {
    const auth = useSelector((state: RootState) => selectAuthCredentials(state));
    const integration = useSelector((state: RootState) =>
        selectIntegrationFromIntegrationType(state, appId, IntegrationType.ExpoEAS),
    ) as IntegrationExpoEAS | undefined;
    const stepConfig = useSelector((state: RootState) => selectStepConfig(state, stepRunComponentProps.stepConfigId));
    const isViewingAppsInDemoMode = useSelector((state: RootState) => selectIsAppsDemoModeEnabled(state));
    const [fetchStepConfig, isFetchingStepConfig] = useGetEntityV2(
        FetchableEntityTypeV2.StepConfig,
        stepRunComponentProps.stepConfigId,
    );
    const configuredInitialPercentage =
        stepConfig?.archetype === ReleaseStepType.Takeoff ?
            stepConfig.settings.expoEAS?.initialRolloutPercentage
        :   undefined;
    const requestedStepConfigKey = `${orgId}:${stepRunComponentProps.workflowId}:${stepRunComponentProps.stepConfigId}`;
    const requestedStepConfigKeyRef = React.useRef<string | undefined>(undefined);
    const publishedByUser = useSelector((state: RootState) =>
        expoData?.publishedBy ? selectUserById(state, orgId, appId, expoData.publishedBy) : undefined,
    );
    const [candidates, setCandidates] = React.useState<ExpoEASCandidateUpdateGroup[]>([]);
    const [selectedSourceGroup, setSelectedSourceGroup] = React.useState(expoData?.sourceGroup);
    const [pendingSourceGroupId, setPendingSourceGroupId] = React.useState<string>();
    const [selectorOpen, setSelectorOpen] = React.useState(false);
    const [fetchingCandidates, setFetchingCandidates] = React.useState(false);
    const [savingSelection, setSavingSelection] = React.useState(false);
    const [candidatesError, setCandidatesError] = React.useState<string>();
    const published = hasProductionPublishData(expoData);

    React.useEffect(() => {
        if (
            configuredInitialPercentage != null ||
            isViewingAppsInDemoMode ||
            isFetchingStepConfig ||
            requestedStepConfigKeyRef.current === requestedStepConfigKey
        ) {
            return;
        }
        requestedStepConfigKeyRef.current = requestedStepConfigKey;
        fetchStepConfig({
            orgId,
            workflowId: stepRunComponentProps.workflowId,
            stepConfigId: stepRunComponentProps.stepConfigId,
        });
    }, [
        configuredInitialPercentage,
        isViewingAppsInDemoMode,
        isFetchingStepConfig,
        requestedStepConfigKey,
        orgId,
        stepRunComponentProps.workflowId,
        stepRunComponentProps.stepConfigId,
        fetchStepConfig,
    ]);

    React.useEffect(() => {
        setSelectedSourceGroup(expoData?.sourceGroup);
    }, [expoData?.sourceGroup]);

    const fetchCandidates = React.useCallback(async () => {
        if (!auth || published) return;
        setFetchingCandidates(true);
        setCandidatesError(undefined);
        try {
            const response = await API.getExpoEASCandidateUpdateGroups(stepRunComponentProps, auth);
            const newestFirst = [...(response.data?.expoEasCandidateUpdateGroups ?? [])].sort(
                (left, right) => dayjs(right.createdAt).valueOf() - dayjs(left.createdAt).valueOf(),
            );
            setCandidates(newestFirst);
        } catch (error) {
            setCandidatesError(error instanceof Error ? error.message : 'Unable to load Expo update groups.');
        } finally {
            setFetchingCandidates(false);
        }
    }, [auth, published, stepRunComponentProps]);

    React.useEffect(() => {
        void fetchCandidates();
    }, [fetchCandidates]);

    const openSelector = () => {
        setPendingSourceGroupId(selectedSourceGroup?.id);
        setSelectorOpen(true);
    };

    const saveSelection = async () => {
        if (!auth || !pendingSourceGroupId) return;
        setSavingSelection(true);
        try {
            const response = await API.setExpoEASSourceUpdateGroup(stepRunComponentProps, pendingSourceGroupId, auth);
            const selected = response.data?.expoEasSourceUpdateGroup;
            if (selected) setSelectedSourceGroup(selected);
            setSelectorOpen(false);
            globalMessage.success('Expo source update group selected.');
        } catch (error) {
            globalMessage.error(error instanceof Error ? error.message : 'Unable to select the Expo update group.');
        } finally {
            setSavingSelection(false);
        }
    };

    const [releaseExpoUpdate, releaseLoading, releaseResponseType, releaseError] = useCallUpdatable(
        StepRunUpdatable.ReleaseAppleVersion,
        stepRunComponentProps,
    );

    React.useEffect(() => {
        if (releaseResponseType === RunwayResponseType.Success) {
            globalMessage.success(`Successfully published the ${displayableVersion} update to Expo.`);
        }
    }, [displayableVersion, releaseResponseType]);

    React.useEffect(() => {
        if (releaseError) globalMessage.error(releaseError);
    }, [releaseError]);

    const channel = integration?.data.channel;
    const initialPercentage = expoData?.initialRolloutPercentage ?? configuredInitialPercentage;
    const initialRolloutCopy =
        initialPercentage != null ? `${initialPercentage}%` : 'the configured initial percentage';
    const publishedBy = publishedByUser ? displayUserNameLong(publishedByUser) : expoData?.publishedBy;

    return (
        <Layout style={{ background: runwayColors.white }}>
            <Content
                style={{
                    ...styles.innerContent,
                    ...styles.contentWithFooterStyle,
                    padding: 24,
                    paddingBottom: 89,
                }}
            >
                <Space direction="vertical" size="large" style={{ width: '100%' }}>
                    <div>
                        <Text
                            style={{
                                fontSize: 30,
                                fontWeight: 600,
                                color: published ? runwayColors.statusGreen : runwayColors.statusGrey,
                            }}
                        >
                            {published ? 'Published to production' : 'Ready to publish to production'}
                        </Text>
                        <br />
                        <Text type="secondary">
                            {published ?
                                `This source update group has already been published to the ${channel ?? 'production'} channel.`
                            :   `Select an existing Expo update group to republish to the ${channel ?? 'configured production'} channel.`
                            }
                        </Text>
                    </div>

                    <Card
                        style={expoReleaseCardStyle}
                        styles={expoReleaseCardStyles}
                        title={<Text style={releaseStepModuleTitleStyle}>Source update group</Text>}
                        extra={
                            !published && (
                                <Button icon={<EditOutlined />} onClick={openSelector}>
                                    {selectedSourceGroup ? 'Change update group selection' : 'Select update group'}
                                </Button>
                            )
                        }
                    >
                        {selectedSourceGroup ?
                            <Space direction="vertical" size="middle" style={{ width: '100%' }}>
                                <UpdateGroupDetails
                                    group={selectedSourceGroup}
                                    platforms={sourcePlatforms(selectedSourceGroup)}
                                />
                            </Space>
                        :   <Empty
                                description="No Expo source update group selected"
                                image={Empty.PRESENTED_IMAGE_SIMPLE}
                            />
                        }
                    </Card>

                    {!published && (
                        <Alert
                            type="info"
                            showIcon
                            message={
                                <>
                                    Rollout will start at {initialRolloutCopy}. You can configure this setting on the{' '}
                                    <Link
                                        to={createAppSettingsRouteWithSettingType(
                                            orgId,
                                            appId,
                                            SettingType.WorkflowSteps,
                                        )}
                                    >
                                        Workflow steps section
                                    </Link>
                                </>
                            }
                        />
                    )}

                    {published && (
                        <Card
                            style={expoReleaseCardStyle}
                            styles={expoReleaseCardStyles}
                            title={<Text style={releaseStepModuleTitleStyle}>Production publish</Text>}
                        >
                            <Descriptions column={1} size="small">
                                {expoData?.productionGroupId && (
                                    <Descriptions.Item label="Production group ID">
                                        <Text code>{expoData.productionGroupId}</Text>
                                    </Descriptions.Item>
                                )}
                                {channel && <Descriptions.Item label="Production channel">{channel}</Descriptions.Item>}
                                {expoData?.currentRolloutPercentage != null && (
                                    <Descriptions.Item label="Current rollout">
                                        {expoData.currentRolloutPercentage}%
                                    </Descriptions.Item>
                                )}
                                {expoData?.publishedAt && (
                                    <Descriptions.Item label="Published">
                                        {dayjs(expoData.publishedAt).format('MMM D, YYYY [at] h:mm A')}
                                        {publishedBy ? ` by ${publishedBy}` : ''}
                                    </Descriptions.Item>
                                )}
                            </Descriptions>
                        </Card>
                    )}
                </Space>
            </Content>

            <Footer style={styles.footerStyle}>
                <PermissionedActionButton
                    releaseId={releaseId}
                    actionType={RunwayUserAction.UAReleaseAppUpdate}
                    type="primary"
                    icon={published ? <CheckCircleFilled /> : <RocketOutlined />}
                    disabled={!selectedSourceGroup || published}
                    loading={releaseLoading}
                    onClick={() =>
                        releaseExpoUpdate({
                            releasePreviousSubmissionToAllUsersFirst: false,
                        } as ReleaseAppleVersionRequest)
                    }
                >
                    {published ? 'Published to production' : `Publish to ${channel ?? 'production'}`}
                </PermissionedActionButton>
            </Footer>

            <Modal
                title="Select Expo source update group"
                open={selectorOpen}
                onCancel={() => setSelectorOpen(false)}
                onOk={saveSelection}
                okText="Save selection"
                okButtonProps={{ disabled: !pendingSourceGroupId, loading: savingSelection }}
                width={720}
            >
                {fetchingCandidates ?
                    <div style={{ padding: 40, textAlign: 'center' }}>
                        <Spin tip="Loading Expo update groups" />
                    </div>
                : candidatesError ?
                    <Alert
                        type="error"
                        showIcon
                        message="Unable to load Expo update groups"
                        description={candidatesError}
                        action={<Button onClick={() => void fetchCandidates()}>Retry</Button>}
                    />
                : candidates.length === 0 ?
                    <Empty description="No eligible Expo update groups found" />
                :   <Select
                        aria-label="Expo source update group"
                        value={pendingSourceGroupId}
                        onChange={setPendingSourceGroupId}
                        placeholder="Select an update group"
                        optionLabelProp="label"
                        style={{ width: '100%' }}
                    >
                        {candidates.map((candidate) => (
                            <Select.Option
                                key={candidate.id}
                                value={candidate.id}
                                label={`${candidate.branchName} — ${candidate.message || candidate.runtimeVersion}`}
                            >
                                <UpdateGroupDetails
                                    group={candidate}
                                    platforms={candidatePlatforms(candidate)}
                                    releasedByVersion={candidate.releasedBy?.internalVersion}
                                />
                            </Select.Option>
                        ))}
                    </Select>
                }
            </Modal>
        </Layout>
    );
};
